import { Container, Divider, Heading, NextLink, Paper, Stack } from "@bilar/ui";
import Head from "next/head";
import {
	BasePageProps,
	useAppTranslation,
	useContentStyles,
} from "@bilar/common";
import {
	GetStaticProps,
	GetStaticPropsContext,
	GetStaticPropsResult,
} from "next";
import { getBasePagePropsData } from "@bilar/pages-common";
import { getConfig, routes } from "@bilar/config";
import React from "react";

type PrivacyPageProps = {} & BasePageProps;

const HowToPage = (props: PrivacyPageProps) => {
	const { list, orderedList, unOrderedList } = useContentStyles();
	const { t } = useAppTranslation();
	const { baseUrl } = getConfig().public;
	return (
		<>
			<Head>
				<title>Bílar.is - Hvernig virkar Bílar.is</title>
				<link
					rel="canonical"
					href={`${baseUrl}/how-it-works`}
					key="canonical"
				/>
				<link
					rel="alternate"
					hrefLang="x-default"
					href={`${baseUrl}/how-it-works`}
					key="alternate-x-default"
				/>
				<link
					rel="alternate"
					hrefLang="is"
					href={`${baseUrl}/how-it-works`}
					key="alternate-is"
				/>
				<link
					rel="alternate"
					hrefLang="en"
					href={`${baseUrl}/en/how-it-works/en`}
					key="alternate-en"
				/>
			</Head>{" "}
			<Container>
				<Heading as="h1" size="large">
					Hvernig virkar Bílar.is
				</Heading>
				<p>
					Velkomin(n) á Bílar.is, besti markaðurinn til að{" "}
					<NextLink href={routes.vehicleClassifieds.new}>
						<strong>skrá smáauglýsingu</strong>
					</NextLink>{" "}
					fyrir ökutækið þitt eða skoða{" "}
					<strong>
						<NextLink href={routes.vehicleClassifieds.index}>
							smáauglýsingar
						</NextLink>
					</strong>{" "}
					fyrir ökutæki.
				</p>
				<p>
					Hvort sem þú vilt selja ökutækið þitt eða á í leit að nýju, einföldum
					við ferlið með því að tengja seljendur við mögulega kaupendur.
				</p>
				<p>Svona geturðu byrjað:</p>

				<Divider my={6} variant="dotted" />

				<Stack orientation={["vertical", "horizontal"]} spacing={12} mb={6}>
					<Paper as="section" width={["100%", "50%"]} p={4}>
						<Heading as="h2">Fyrir seljendur</Heading>

						<ol css={[list.root, orderedList.root]}>
							<li css={[list.item, orderedList.item]}>
								<Heading as="h3" size="small">
									Búðu til auglýsinguna þína
								</Heading>
								<ul css={[list.root, unOrderedList.root]}>
									<li css={[list.item, unOrderedList.item]}>
										<p>
											<strong>Skráðu auglýsingu</strong>: Veldu valkostinn{" "}
											<NextLink href={routes.vehicleClassifieds.new}>
												{t("postNewAd")}
											</NextLink>
											, búðu til notandaaðgang og gefðu upp nauðsynlegar
											upplýsingar um ökutækið þitt, svo sem tegund, gerð,
											árgerð, verð o.s.frv.{" "}
										</p>
										<p>
											Við mælum líka með að þú skrifar góða lýsingu í textareit
											þegar þú skráir upplýsingar.
										</p>
										<p>
											Mundu, að bæta við góðum myndum af ökutækinu getur
											verulega aukið sýnileika og aðdráttarafl auglýsingarinnar
											þinnar.
										</p>
										<p>
											Auk þess að hlaða upp myndum, hefur þú aðgang að
											myndritlinum okkar (image editor), sem gerir þér kleift að
											sýsla með myndirnar þínar.{" "}
										</p>
										<p>
											Þetta tól er sérstaklega gagnlegt til þess að afmá
											viðkvæmar upplýsingar, eins og{" "}
											<strong>númeraplötu</strong> ökutækisins.
										</p>
									</li>
								</ul>
							</li>
							<li css={[list.item, orderedList.item]}>
								<Heading as="h3" size="small">
									Bíddu eftir eftirspurn
								</Heading>

								<ul css={[list.root, unOrderedList.root]}>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Vertu aðgengileg(ur):</strong> Gakktu úr skugga um
										að gefa upp nákvæmar tengiliðsupplýsingar í auglýsingunni
										þinni, svo sem símanúmer eða netfang, svo áhugasamir
										kaupendur geti haft samband við þig beint.
									</li>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Stjórnaðu auglýsingunum þínum:</strong> Í prófílnum
										þínum er síða sem heitir{" "}
										<NextLink href={routes.profile.classifieds}>
											{t("myClassifieds")}
										</NextLink>
										, þar geturðu stjórnað auglýsingunum þínum, uppfært
										upplýsingar, eytt eða aftengt þær.
									</li>
								</ul>
							</li>
							<li css={[list.item, orderedList.item]}>
								<Heading as="h3" size="small">
									Ljúktu við sölu
								</Heading>

								<ul css={[list.root, unOrderedList.root]}>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Bein samskipti:</strong> Allar samningaviðræður,
										skoðanir og sölur eru framkvæmdar beint á milli þín og
										kaupandans.
									</li>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Örugg viðskiptaráð:</strong> Við mælum með því að
										hittast á opinberum stöðum fyrir skoðanir og viðskipti,
										staðfestu auðkenni kaupanda ásamt því að fylgja bestu venjum
										fyrir örugga greiðslu.
										<br />
										<br />
										<p>
											Sjáðu síðuna{" "}
											<NextLink
												href="https://island.is/bifreidakaup"
												target="_blank"
											>
												Kaup og sala á ökutæki
											</NextLink>{" "}
											hjá Samgöngustofu.
										</p>
									</li>
								</ul>
							</li>
						</ol>
					</Paper>
					<Paper as="section" width={["100%", "50%"]} p={4}>
						<Heading as="h2">Fyrir kaupendur</Heading>
						<ol css={[list.root, orderedList.root]}>
							<li css={[list.item, orderedList.item]}>
								<Heading as="h3" size="small">
									Finndu rétta ökutækið
								</Heading>
								<ul css={[list.root, unOrderedList.root]}>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Skoðaðu ökutæki:</strong>{" "}
										<NextLink href={routes.vehicleClassifieds.index}>
											{t("viewAllVehicles")}
										</NextLink>{" "}
										eða notaðu leitar- og síunartól á Bílar.is til að finna
										ökutækið sem hentar þínum þörfum. Þú getur raðað skráningum
										eftir gerð, módeli, verði, aldri og fleira.
									</li>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Upplýsingar um ökutæki:</strong> Smelltu á hvaða
										skráningu sem er til að sjá ítarlegar upplýsingar og skoða
										myndir af ökutækinu.
									</li>
								</ul>
							</li>
							<li css={[list.item, orderedList.item]}>
								<Heading as="h3" size="small">
									Hafðu samband við seljandann
								</Heading>

								<ul css={[list.root, unOrderedList.root]}>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Haftu samband:</strong> Þegar þú finnur ökutæki sem
										þú hefur áhuga á, hafðu þá beint samband við seljandann með
										því að nota upplýsingarnar sem gefnar eru í skráningunni,
										venjulega í gegnum símtal eða tölvupóst.
									</li>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Spurðu spurninga:</strong> Ekki hika við að biðja
										seljandann um frekari upplýsingar um ökutækið, auka myndir,
										eða að skipuleggja skoðun eða prófunarakstur.
									</li>
								</ul>
							</li>
						</ol>
					</Paper>
				</Stack>
			</Container>
		</>
	);
};
export const getStaticProps: GetStaticProps<PrivacyPageProps> = async (
	ctx: GetStaticPropsContext,
): Promise<GetStaticPropsResult<PrivacyPageProps>> => {
	// Get Locale and common translations
	const baseProps = await getBasePagePropsData(ctx);

	return {
		props: {
			...baseProps,
		},
		// we will attempt to re-generate the page:
		// - when a request comes in
		// - at most once every second
		//revalidate: 60,
	};
};
export default HowToPage;
